import superagent, { Request, Response, ResponseError } from 'superagent';
import { dissoc } from 'ramda';

import stringify from '../json/stringify';

declare module 'superagent' {
    interface SuperAgentStatic {
        getXHR?: () => XMLHttpRequest;
    }
}

export type RequestHeader = {
    [x: string]: string | (() => string);
};

type RequestAuth = {
    bearerToken: (() => string | undefined) | string | undefined;
    refreshAuth: () => Promise<unknown>;
    errorCallback: () => void;
};

type RequestOptions = {
    header?: Record<string, string>;
    auth?: RequestAuth;
    query?: Record<string, any>;
    responseType?: string;
};

type RequestGlobalOptions = {
    apiUrl: string;
};

const DEFAULT_GLOBAL_OPTIONS: RequestGlobalOptions = { apiUrl: '' };

const DEFAULT_OPTIONS: RequestOptions = { header: {}, query: {} };

let globalOptions;
let globalHeader: RequestHeader;
let globalAuth: RequestAuth;
let CachedCleanXHR: typeof XMLHttpRequest;
let cleanXHRIframe: HTMLIFrameElement | null = null;

const createCleanXMLHttpRequest = async (): Promise<typeof XMLHttpRequest> => new Promise((resolve, reject) => {
    if (cleanXHRIframe?.contentWindow && (cleanXHRIframe.contentWindow as Window & typeof globalThis).XMLHttpRequest) {
        resolve((cleanXHRIframe.contentWindow as Window & typeof globalThis).XMLHttpRequest);
    }

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.srcdoc = '';

    iframe.onload = function() {
        try {
            const NativeXMLHttpRequest = iframe.contentWindow && (iframe.contentWindow as Window & typeof globalThis).XMLHttpRequest;
            if (NativeXMLHttpRequest) {
                // Note that we cannot remove the iframe or clone the XMLHttpRequest otherwise the requests will not work
                cleanXHRIframe = iframe;
                resolve(NativeXMLHttpRequest);
            } else {
                iframe.remove();
                reject(new Error('Could not retrieve clean XMLHttpRequest from iframe'));
            }
        } catch (err) {
            iframe.remove();
            reject(err);
        }
    };

    iframe.onerror = (err) => {
        iframe.remove();
        reject(err);
    };

    document.body.appendChild(iframe);
});

const isXMLHttpRequestMonkeyPatched = () => {
    const xhrDesc = Object.getOwnPropertyDescriptor(XMLHttpRequest.prototype, 'open');

    if (!xhrDesc || typeof xhrDesc.value !== 'function') {
        return false;
    }

    const functionSource = xhrDesc.value.toString();

    return !functionSource.includes('[native code]');
}

const setCleanXHR = (xhr: typeof XMLHttpRequest) => {
    CachedCleanXHR = xhr

    superagent.getXHR = function () {
        return new CachedCleanXHR();
    };
}

const setOptions = (definition: RequestGlobalOptions = DEFAULT_GLOBAL_OPTIONS): void => {
    globalOptions = definition;
};

const setHeader = (definition: RequestHeader = {}): void => {
    globalHeader = definition;
};

const updateHeader = (definition: RequestHeader = {}): void => {
    globalHeader = {
        ...globalHeader,
        ...definition,
    };
};

const setAuth = (definition: RequestAuth): void => {
    globalAuth = definition;
};

export const prefixApiUrl = (apiUrl: string, url: string): string => {
    const fixedPrefix = apiUrl[apiUrl.length - 1] === '/' ? apiUrl : `${apiUrl}/`;

    return url[0] === '/'
        ? `${fixedPrefix}${url.slice(1)}`
        : url.indexOf('://') === -1
        ? `${fixedPrefix}${url}`
        : url;
};

const header = (agent: Request, definition: RequestHeader = {}): void => {
    Object.entries(definition).forEach(([headerName, headerValue]) => {
        const value = typeof headerValue === 'function' ? headerValue() : headerValue;
        agent.set(headerName, value);
    });
};

const responseType = (response: Request, type = ''): void => {
    if (type) {
        response.responseType(type);
    }
};

const auth = async (agent: Request, definition: RequestAuth): Promise<void> => {
    if (definition.errorCallback) {
        const callback = (err: ResponseError): void => {
            if (err.status === 401) {
                definition.errorCallback();
            }
        };
        agent.on('error', callback);
    }

    if (definition.refreshAuth) {
        try {
            await definition.refreshAuth();
        } catch (err) {
            console.error(err);
        }
    }

    if (definition.bearerToken) {
        const token =
            typeof definition.bearerToken === 'function'
                ? definition.bearerToken()
                : definition.bearerToken;
        agent.set('Authorization', `Bearer ${token}`);
    }
};

const query = (agent: Request, queries = {}): void => {
    if (Object.keys(queries).length) {
        agent.query(queries);
    }
};

const getAgent = () => superagent.agent();

const get = async (url: string, options: RequestOptions = DEFAULT_OPTIONS): Promise<Response> => {
    const agent = getAgent();

    await auth(agent, { ...globalAuth, ...options.auth });
    header(agent, { ...globalHeader, ...options.header });
    query(agent, options.query);

    const response = agent
        .get(prefixApiUrl(globalOptions.apiUrl, url));

    responseType(response, options.responseType);
    return response;
};

const post = async (
    url: string,
    data: string | Record<string, any>,
    options: RequestOptions = DEFAULT_OPTIONS
): Promise<Response> => {
    const agent = getAgent();

    await auth(agent, { ...globalAuth, ...options.auth });
    header(agent, { ...globalHeader, ...options.header });
    query(agent, options.query);

    const payload = typeof data === 'string' ? data : stringify(data);

    const response = agent.post(prefixApiUrl(globalOptions.apiUrl, url)).send(payload);
    responseType(response, options.responseType);
    return response;
};

const put = async (
    url: string,
    data: string | Record<string, any>,
    options: RequestOptions = DEFAULT_OPTIONS
): Promise<Response> => {
    const agent = getAgent();

    await auth(agent, { ...globalAuth, ...options.auth });
    header(agent, { ...globalHeader, ...options.header });
    query(agent, options.query);

    const payload = typeof data === 'string' ? data : stringify(data);

    const response = agent.put(prefixApiUrl(globalOptions.apiUrl, url)).send(payload);
    responseType(response, options.responseType);
    return response;
};

const del = async (url: string, options: RequestOptions = DEFAULT_OPTIONS): Promise<Response> => {
    const agent = getAgent();

    await auth(agent, { ...globalAuth, ...options.auth });
    header(agent, { ...globalHeader, ...options.header });
    query(agent, options.query);

    const response = agent.del(prefixApiUrl(globalOptions.apiUrl, url));
    responseType(response, options.responseType);
    return response;
};

const upload = async (
    url: string,
    attachment: {
        name: string;
        file: Blob | Buffer | string | boolean | number;
        filename?: string;
        fields?: Record<string, any>;
    },
    options: RequestOptions = DEFAULT_OPTIONS
): Promise<Response> => {
    const agent = getAgent();

    const requestHeaders = { ...globalHeader, ...options.header };

    await auth(agent, { ...globalAuth, ...options.auth });
    header(agent, dissoc('Content-Type', requestHeaders));
    query(agent, options.query);

    const { file, name, filename, fields = {} } = attachment;

    const response = agent.post(prefixApiUrl(globalOptions.apiUrl, url));

    Object.entries(fields).forEach(([key, value]) => {
        response.field(key, value);
    });

    response.attach(name, file, filename);

    responseType(response, options.responseType);
    return response;
};

const keepAliveFetch = async (url: string, data: string): Promise<void> => {
    if (fetch !== undefined) {
        const headers = new Headers();
        Object.entries(globalHeader).forEach(([headerName, headerValue]) => {
            const value = typeof headerValue === 'function' ? headerValue() : headerValue;
            headers.set(headerName, value);
        });

        await fetch(prefixApiUrl(globalOptions.apiUrl, url), {
            method: 'POST',
            headers,
            body: data,
            keepalive: true,
        });
    } else {
        await post(url, data);
    }
};

const ApiRequest = {
    setOptions,
    setHeader,
    updateHeader,
    setAuth,
    get,
    post,
    put,
    del,
    upload,
    keepAliveFetch,
    prefixApiUrl,
    isXMLHttpRequestMonkeyPatched,
    createCleanXMLHttpRequest,
    setCleanXHR,
};

export default ApiRequest;
